import { useMetricsContext } from 'modules/productAnalytics/context';
import { FC, useEffect, useState } from 'react';
import { CampaignFilterDefaults, HighLevelMetricDefaults } from '../constants';
import {
  filterMetricsByMedium,
  filterSelectedMetricsByMedium,
  getHighLevelAndMedMetricsFromMetrics,
} from '../utils';
import { CampaignAndMetricFiltersContext } from './CampaignAndMetricFiltersContext';

export const CampaignAndMetricFiltersContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { analyticsMetrics, highLevelMetrics } = useMetricsContext();
  const [selectedMediums, setSelectedMediums] = useState(['Email']);
  const [campaignFilters, setCampaignFilters] = useState(CampaignFilterDefaults);
  const [appliedCampaignFilters, setAppliedCampaignFilters] = useState(CampaignFilterDefaults);
  const [previousCampaignTableFilters, setPreviousCampaignTableFilters] = useState('');
  const [previousAudienceFilters, setPreviousAudienceFilters] = useState('');
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  const filteredHLMDefaults = filterSelectedMetricsByMedium(
    selectedMediums,
    HighLevelMetricDefaults,
    highLevelMetrics,
    analyticsMetrics,
  );
  const [selectedCrossMetrics, setSelectedCrossMetrics] = useState(filteredHLMDefaults);
  /**
   * Cross metrics categorized into overall or medium metrics and reformatted as
   * an array of {enumName: [Overall Metric Name]} or {name: [Medium Metric Name]}, respectively
   */

  const { mediumMetrics, overallMetrics } = getHighLevelAndMedMetricsFromMetrics(
    selectedCrossMetrics,
    highLevelMetrics,
    analyticsMetrics,
  );

  const MediumFilteredHLMs = new Set(
    filterMetricsByMedium(selectedMediums, highLevelMetrics).map((el) => el.metricName),
  );
  const MediumFilteredAMs = new Set(
    filterMetricsByMedium(selectedMediums, analyticsMetrics).map((el) => el.metricName),
  );

  /**
   * We sets `isFiltersInitialized` to true when the context is fully configured.
   * Ensuring the component state reflects the readiness of the context to be used.
   */
  useEffect(() => {
    if (
      filteredHLMDefaults.length > 0 &&
      campaignFilters &&
      appliedCampaignFilters &&
      selectedMediums.length > 0
    ) {
      setIsFiltersInitialized(true);
    }
  }, [filteredHLMDefaults, selectedMediums, campaignFilters, appliedCampaignFilters]);

  return (
    <CampaignAndMetricFiltersContext.Provider
      value={{
        filteredHLMDefaults,
        campaignFilters,
        setCampaignFilters,
        selectedMediums,
        setSelectedMediums,
        selectedCrossMetrics,
        setSelectedCrossMetrics,
        mediumMetrics,
        overallMetrics,
        appliedCampaignFilters,
        setAppliedCampaignFilters,
        MediumFilteredHLMs,
        MediumFilteredAMs,
        previousCampaignTableFilters,
        setPreviousCampaignTableFilters,
        previousAudienceFilters,
        setPreviousAudienceFilters,
        isFiltersInitialized,
      }}
    >
      {children}
    </CampaignAndMetricFiltersContext.Provider>
  );
};
